import type { DelmanDayAttendance } from '@/features/delman/storage/delmanAttendanceStorage'
import type { AttendanceEventRecord } from '@/features/kitchen/storage/attendanceStorage'

export type ServerAttendanceStatus = 'not_checked_in' | 'checked_in' | 'checked_out'

/** Bentuk respons GET /attendance/today dan snapshot `attendance` di konflik 409. */
export type ServerAttendance = {
  date: string
  status: ServerAttendanceStatus
  check_in_at: string | null
  check_out_at: string | null
  check_in_photo_url?: string | null
  check_out_photo_url?: string | null
  on_time?: boolean | null
}

export type DelmanAttendanceAction = 'check_in' | 'check_out'

export type DelmanAttendanceIdentity = Pick<
  DelmanDayAttendance,
  'delmanId' | 'delmanName' | 'kitchenName' | 'date' | 'shiftStart' | 'shiftEnd'
>

export type ReconcileInput = {
  server: ServerAttendance
  local: DelmanDayAttendance | null
  /** Aksi absensi delman ini yang masih ada di antrean sinkron. */
  queuedActions: DelmanAttendanceAction[]
  identity: DelmanAttendanceIdentity
}

export type ReconcileResult = {
  /** null = tidak ada absensi hari ini; cache lokal harus dihapus. */
  record: DelmanDayAttendance | null
  /** Aksi antrean yang sudah tercermin di server dan boleh dihapus. */
  resolvedActions: DelmanAttendanceAction[]
}

function formatClock(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function serverEvent(
  at: string,
  onTime: boolean | undefined,
  local: AttendanceEventRecord | undefined,
): AttendanceEventRecord {
  return { at, timeLabel: formatClock(at), photoUrl: local?.photoUrl ?? null, onTime }
}

export function isServerAttendance(value: unknown): value is ServerAttendance {
  if (!value || typeof value !== 'object') return false
  const status = (value as { status?: unknown }).status
  return status === 'not_checked_in' || status === 'checked_in' || status === 'checked_out'
}

/**
 * Server adalah sumber kebenaran. Transisi lokal hanya dipertahankan selama
 * masih ada di antrean dan server belum mencerminkannya.
 */
export function reconcileDelmanAttendance(input: ReconcileInput): ReconcileResult {
  const { server, local, queuedActions, identity } = input
  const queued = new Set(queuedActions)

  const pendingCheckIn = queued.has('check_in') && server.status === 'not_checked_in'
  const pendingCheckOut = queued.has('check_out') && server.status !== 'checked_out'

  const resolvedActions: DelmanAttendanceAction[] = []
  if (queued.has('check_in') && !pendingCheckIn) resolvedActions.push('check_in')
  if (queued.has('check_out') && !pendingCheckOut) resolvedActions.push('check_out')

  const checkIn = server.check_in_at
    ? serverEvent(server.check_in_at, server.on_time ?? undefined, local?.checkIn)
    : pendingCheckIn
      ? local?.checkIn
      : undefined
  const checkOut = server.check_out_at
    ? serverEvent(server.check_out_at, undefined, local?.checkOut)
    : pendingCheckOut
      ? local?.checkOut
      : undefined

  if (!checkIn && !checkOut) {
    return { record: null, resolvedActions }
  }

  let pendingAction: DelmanAttendanceAction | undefined
  if (pendingCheckOut && checkOut && !server.check_out_at) pendingAction = 'check_out'
  else if (pendingCheckIn && checkIn && !server.check_in_at) pendingAction = 'check_in'

  const record: DelmanDayAttendance = { ...identity, checkIn, checkOut }
  if (pendingAction) record.pendingAction = pendingAction
  return { record, resolvedActions }
}
